import {Component, OnInit, ViewChild} from '@angular/core';
import {Observable} from 'rxjs';
import {Invite, Player} from 'gamesis-story-client';
import {ActionSheetController, AlertController, IonRefresher} from '@ionic/angular';
import {Device} from '@capacitor/device';
import {Share} from '@capacitor/share';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {
    createInvite,
    createInviteSuccess,
    deletePlayer,
    leaveCampaign,
    loadPlayers,
    revokeInvite,
    selectInvites,
    selectMe,
    selectOtherPlayers,
    selectPlayersLoading,
    toggleGM,
    updateMe
} from 'gamesis-state';
import {Destroyable} from '../components/destroyable';
import {Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';

@Component({
    template: `
        <ion-content fullscreen="true">
            <div class="ion-safe-top"></div>
            <ion-refresher #refresher
                           slot="fixed"
                           pull-min="100"
                           class="ion-safe-top"
                           (ionRefresh)="refresh()">
                <ion-refresher-content
                        pullingIcon="refresh-sharp"
                        pullingText="Pull to refresh..."
                        refreshingText="Refreshing..."
                        refreshing-spinner="circular"></ion-refresher-content>
            </ion-refresher>
            @if (me$ | async) {
                <ion-list lines="none"
                          inset>
                    <ion-item lines="full"
                              color="light"
                              class="ion-align-items-center">
                        <app-input [control]="nameControl"
                                   label="Display Name"
                                   cssClass="monospaced"
                                   errorText="Must be between 3 - 50 characters"
                                   minlength="3"
                                   maxlength="50">
                        </app-input>
                        <ion-text slot="end"
                                  class="monospaced ion-text-end"
                                  [color]="(me$ | async).gameMaster ? 'primary':'medium'">GM
                        </ion-text>
                    </ion-item>
                </ion-list>
            }


            @if ((players$ | async)?.length === 0 && (invites$ | async)?.length === 0) {
                <app-label class="monospaced">
                    It looks like you are here all alone :-(<br>
                    Click the + button and invite some friends to play with you.
                </app-label>
            }

            @if ((players$ | async).length > 0) {
                <app-label class="monospaced">
                    Others
                </app-label>
                <ion-list inset>
                    @for (player of players$ | async | sort: 'name'; track player.id) {
                        <ion-item color="light" lines="full">
                            <ion-label class="monospaced">
                                {{ player.name ? player.name : player.id }}
                            </ion-label>
                            <ng-container *gm>
                                <ion-button slot="end"
                                            (click)="toggleGm(player)"
                                            class="monospaced ion-text-end"
                                            fill='clear'
                                            [color]="player.gameMaster ? 'primary':'medium'">GM
                                </ion-button>
                                <ion-icon slot="end"
                                          name="remove-circle"
                                          (click)="delete(player)"></ion-icon>
                            </ng-container>
                        </ion-item>
                    }
                </ion-list>
            }
            <ng-container *gm>
                @if ((invites$ | async).length > 0) {
                    <app-label class="monospaced">
                        Pending invites
                    </app-label>
                }
                <ion-list inset>
                    @for (invite of invites$ | async; track invite.id) {
                        <ion-item lines="full" color="light">
                            <ion-label (click)="share(invite)">
                                <ion-label class="monospaced">{{ invite.code }}</ion-label>
                                <ion-text color="dark">
                                    <small>valid until: {{ invite.expirationDate | date:'yyyy-MM-dd HH:mm' }}</small>
                                </ion-text>
                            </ion-label>
                            <ion-icon slot="end" name="remove-circle" (click)="revoke(invite)"></ion-icon>
                        </ion-item>
                    }

                    <ion-item button
                              detail="false"
                              class="monospaced"
                              color="dark"
                              (click)="invite()">
                        <ion-icon name="add" slot="start"></ion-icon>
                        <ion-label>Add invite</ion-label>
                    </ion-item>
                </ion-list>
            </ng-container>

            <ion-list inset class="ion-safe-bottom">
                <ion-button type="button"
                            color="primary"
                            expand='block'
                            class="ion-no-margin"
                            (click)="showMenu()">Leave Campaign
                </ion-button>
            </ion-list>
        </ion-content>
    `,
    standalone: false
})
export class PlayersPage extends Destroyable implements OnInit {
    @ViewChild('refresher')
    refresher!: IonRefresher;
    public players$: Observable<Player[]>;
    public invites$: Observable<Invite[]>;
    public nameControl: FormControl;
    public me$: Observable<Player>;

    constructor(private alertController: AlertController,
                private actionSheetCtrl: ActionSheetController,
                private store: Store,
                private actions$: Actions,
                private formBuilder: FormBuilder) {
        super();
    }

    ngOnInit() {
        this.nameControl = this.formBuilder.control(null, [
            Validators.required,
            Validators.minLength(3), Validators.maxLength(50),
            Validators.pattern('[^\\s]+(\\s+[^\\s]+)*')
        ]);

        this.nameControl.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            if (this.nameControl.valid) {
                this.store.dispatch(updateMe({name: this.nameControl.value}));
            }
        });

        this.players$ = this.store.select(selectOtherPlayers);
        this.invites$ = this.store.select(selectInvites);
        this.me$ = this.store.select(selectMe);
        this.me$
            .pipe(
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe(player => {
                this.nameControl.patchValue(player?.name, {emitEvent: false});
            });

        this.actions$
            .pipe(
                ofType(createInviteSuccess),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe((action) => {
                this.share(action.invite);
            });

        this.store.select(selectPlayersLoading)
            .pipe(
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe((loading) => {
                if (!loading && this.refresher) {
                    this.refresher.complete();
                }
            });
    }

    delete(player: Player) {
        this.store.dispatch(deletePlayer({id: player.id}));
    }

    revoke(invite: Invite) {
        this.store.dispatch(revokeInvite({id: invite.id}));
    }

    toggleGm(player: Player) {
        this.store.dispatch(toggleGM({player: player}));
    }

    invite(): void {
        this.store.dispatch(createInvite());
    }

    share(invite: Invite): void {
        const buttons: any[] = [{
            text: 'OK'
        }];
        Device.getInfo().then(info => {
            if (info.platform !== 'web') {
                buttons.unshift({
                    text: 'Share',
                    handler: () => {
                        Share.share({
                            title: 'Here is a ga.me.sis invitation code',
                            text: invite.code,
                            dialogTitle: 'Share invitation code',
                        });
                        return false;
                    }
                });
            } else if (navigator.clipboard) {
                buttons.unshift({
                    text: 'Copy to clipboard',
                    handler: () => {
                        navigator.clipboard.writeText(invite.code);
                        return false;
                    }
                });
            }
        });

        this.alertController.create({
            header: 'Share invitation code',
            subHeader: invite.code,
            message: 'Share this code with other players. It can only be used once',
            buttons: buttons
        }).then(actionSheet => actionSheet.present());
    }

    showMenu(): void {
        this.actionSheetCtrl.create({
            header: 'Are you sure',
            subHeader: 'You will no longer have access to this campaign',
            buttons: [{
                text: 'Leave campaign',
                role: 'destructive',
                handler: () => {
                    this.store.dispatch(leaveCampaign());
                }
            }, {
                text: 'Cancel',
                role: 'cancel'
            }],
        }).then((sheet) => sheet.present());
    }

    refresh() {
        this.store.dispatch(loadPlayers());
    }
}
